import { useEffect, useMemo, useState, useCallback } from "react";
import "./App.css";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import {
  Plus, Search, Sun, Moon, RefreshCw, LayoutGrid, Boxes, Layers,
} from "lucide-react";
import { Toaster } from "./components/ui/sonner";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./components/ui/alert-dialog";
import { ToolCard } from "./components/hub/ToolCard";
import { ToolFormDialog } from "./components/hub/ToolFormDialog";

// Empty by default: frontend and API share the same Cloudflare Pages
// deployment/origin, so a relative "/api" path is correct without a
// separate backend URL. Only set REACT_APP_BACKEND_URL for a genuinely
// different origin (e.g. local dev against a remote preview).
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const useTheme = () => {
  const [dark, setDark] = useState(() => {
    const saved = localStorage.getItem("devhub-theme");
    if (saved) return saved === "dark";
    return window.matchMedia?.("(prefers-color-scheme: dark)").matches ?? false;
  });
  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("devhub-theme", dark ? "dark" : "light");
  }, [dark]);
  return [dark, () => setDark((d) => !d)];
};

function App() {
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [activeCat, setActiveCat] = useState("Tutti");
  const [dark, toggleTheme] = useTheme();

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [resetOpen, setResetOpen] = useState(false);

  const fetchTools = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/tools`);
      setTools(data);
    } catch (e) {
      toast.error("Errore nel caricamento degli strumenti");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTools(); }, [fetchTools]);

  const categories = useMemo(() => {
    const set = new Set(tools.map((t) => t.category));
    return ["Tutti", ...Array.from(set)];
  }, [tools]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return tools
      .filter((t) => activeCat === "Tutti" || t.category === activeCat)
      .filter((t) =>
        !q ||
        t.name.toLowerCase().includes(q) ||
        t.category.toLowerCase().includes(q) ||
        (t.note || "").toLowerCase().includes(q) ||
        t.url.toLowerCase().includes(q)
      )
      .sort((a, b) => (b.isPinned === a.isPinned ? 0 : b.isPinned ? 1 : -1));
  }, [tools, query, activeCat]);

  const handleSubmit = async (payload) => {
    try {
      if (editing) {
        const { data } = await axios.put(`${API}/tools/${editing.id}`, payload);
        setTools((prev) => prev.map((t) => (t.id === data.id ? data : t)));
        toast.success("Strumento aggiornato");
      } else {
        const { data } = await axios.post(`${API}/tools`, payload);
        setTools((prev) => [...prev, data]);
        toast.success("Strumento aggiunto");
      }
      setFormOpen(false);
      setEditing(null);
    } catch {
      toast.error("Operazione non riuscita");
    }
  };

  const handleTogglePin = async (tool) => {
    try {
      const { data } = await axios.put(`${API}/tools/${tool.id}`, { isPinned: !tool.isPinned });
      setTools((prev) => prev.map((t) => (t.id === data.id ? data : t)));
      toast.success(data.isPinned ? "Fissato in alto" : "Rimosso dai fissati");
    } catch {
      toast.error("Operazione non riuscita");
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    try {
      await axios.delete(`${API}/tools/${deleting.id}`);
      setTools((prev) => prev.filter((t) => t.id !== deleting.id));
      toast.success("Strumento eliminato");
    } catch {
      toast.error("Eliminazione non riuscita");
    } finally {
      setDeleting(null);
    }
  };

  const confirmReset = async () => {
    try {
      const { data } = await axios.post(`${API}/tools/reset`);
      setTools(data);
      setActiveCat("Tutti");
      toast.success("Strumenti di default ripristinati");
    } catch {
      toast.error("Ripristino non riuscito");
    } finally {
      setResetOpen(false);
    }
  };

  const openNew = () => { setEditing(null); setFormOpen(true); };
  const openEdit = (tool) => { setEditing(tool); setFormOpen(true); };

  const pinnedCount = tools.filter((t) => t.isPinned).length;

  return (
    <div className="App min-h-screen bg-background">
      <Toaster position="top-center" richColors />

      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-border bg-background/80 backdrop-blur-md">
        <div className="mx-auto flex h-20 max-w-7xl items-center justify-between gap-4 px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary text-primary-foreground">
              <Boxes className="h-6 w-6" />
            </div>
            <div>
              <h1 className="font-display text-lg font-extrabold tracking-tight text-foreground">DevHub</h1>
              <p className="hidden text-xs text-muted-foreground sm:block">Postumia Place</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              data-testid="reset-tools-button"
              variant="outline" size="icon"
              onClick={() => setResetOpen(true)}
              title="Ripristina strumenti di default"
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
            <Button
              data-testid="theme-toggle-button"
              variant="outline" size="icon"
              onClick={toggleTheme}
              title="Cambia tema"
            >
              {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
            <Button data-testid="add-tool-button" onClick={openNew} className="gap-2">
              <Plus className="h-4 w-4" /> <span className="hidden sm:inline">Aggiungi App</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {/* Hero + stats */}
        <div className="mb-8 flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h2 className="font-display text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">
              I tuoi strumenti, un unico posto
            </h2>
            <p className="mt-2 max-w-xl text-sm text-muted-foreground sm:text-base">
              Naviga rapidamente tra GitHub, Cloudflare, Supabase e gli altri servizi che usi.
            </p>
          </div>
          <div className="flex gap-3">
            <div className="rounded-xl border border-border bg-card px-4 py-3">
              <div className="flex items-center gap-2 text-muted-foreground"><Layers className="h-4 w-4" /><span className="text-xs">Strumenti</span></div>
              <p className="mt-1 font-display text-2xl font-bold text-foreground" data-testid="stat-total">{tools.length}</p>
            </div>
            <div className="rounded-xl border border-border bg-card px-4 py-3">
              <div className="flex items-center gap-2 text-muted-foreground"><LayoutGrid className="h-4 w-4" /><span className="text-xs">Fissati</span></div>
              <p className="mt-1 font-display text-2xl font-bold text-foreground" data-testid="stat-pinned">{pinnedCount}</p>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-5">
          <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="search-tools-input"
            placeholder="Cerca per nome, categoria o note..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="h-12 rounded-xl pl-11 text-base"
          />
        </div>

        {/* Category filters */}
        <div className="mb-8 flex flex-wrap gap-2">
          {categories.map((cat) => (
            <button
              key={cat}
              data-testid={`category-tab-${cat}`}
              onClick={() => setActiveCat(cat)}
              className={`rounded-full border px-4 py-1.5 text-sm font-medium transition-all ${
                activeCat === cat
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border bg-card text-muted-foreground hover:border-primary/40 hover:text-foreground"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Grid */}
        {loading ? (
          <div className="py-24 text-center text-muted-foreground">Caricamento…</div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-border py-24 text-center" data-testid="empty-state">
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-secondary text-muted-foreground">
              <Boxes className="h-8 w-8" />
            </div>
            <h3 className="mt-4 font-display text-xl font-semibold text-foreground">
              {tools.length === 0 ? "Nessuno strumento ancora" : "Nessun risultato"}
            </h3>
            <p className="mt-1 max-w-sm text-sm text-muted-foreground">
              {tools.length === 0
                ? "Aggiungi il tuo primo strumento."
                : "Prova a modificare la ricerca o il filtro categoria."}
            </p>
            {tools.length === 0 && (
              <Button className="mt-6 gap-2" onClick={openNew} data-testid="empty-add-button">
                <Plus className="h-4 w-4" /> Aggiungi il tuo primo strumento
              </Button>
            )}
          </div>
        ) : (
          <motion.div layout className="grid grid-cols-1 gap-5 sm:gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            <AnimatePresence mode="popLayout">
              {filtered.map((tool, i) => (
                <ToolCard
                  key={tool.id}
                  tool={tool}
                  index={i}
                  onEdit={openEdit}
                  onDelete={setDeleting}
                  onTogglePin={handleTogglePin}
                />
              ))}
            </AnimatePresence>
          </motion.div>
        )}
      </main>

      <footer className="border-t border-border py-6">
        <p className="text-center text-xs text-muted-foreground">DevHub · Postumia Place</p>
      </footer>

      <ToolFormDialog
        open={formOpen}
        onOpenChange={(v) => { setFormOpen(v); if (!v) setEditing(null); }}
        onSubmit={handleSubmit}
        initial={editing}
      />

      <AlertDialog open={Boolean(deleting)} onOpenChange={(v) => !v && setDeleting(null)}>
        <AlertDialogContent data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare "{deleting?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              Questa azione non può essere annullata. Lo strumento verrà rimosso definitivamente dal tuo hub.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-button">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="delete-confirm-button" className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={resetOpen} onOpenChange={setResetOpen}>
        <AlertDialogContent data-testid="reset-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Ripristinare gli strumenti di default?</AlertDialogTitle>
            <AlertDialogDescription>
              Verranno rimossi tutti gli strumenti attuali e ripristinati quelli di base (GitHub, Cloudflare, Zoho Mail, Resend, Supabase, Postumia Place, Pannello Admin, Postumia Place — Test, Pannello Admin — Test).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="reset-cancel-button">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={confirmReset} data-testid="reset-confirm-button">Ripristina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default App;
